import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, MenuItem, Select,
  FormControl, InputLabel, CircularProgress, Chip, Alert,
  Divider, Stack, TextField, InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CLOUD_FUNCTION_BASE = 'https://us-central1-mylocalforce-295b8.cloudfunctions.net';

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;

  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const asFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCoords = (coords) => {
  if (!coords) return null;

  let lat = asFiniteNumber(coords.lat ?? coords.latitude ?? coords._lat);
  let lng = asFiniteNumber(
    coords.lng ??
    coords.longitude ??
    coords.lon ??
    coords.long ??
    coords._long ??
    coords._lng,
  );

  if (lat === null || lng === null) return null;

  const latInRange = lat >= -90 && lat <= 90;
  const lngInRange = lng >= -180 && lng <= 180;
  const couldBeSwapped =
    (!latInRange && lng >= -90 && lng <= 90 && lat >= -180 && lat <= 180) ||
    (latInRange && lngInRange && Math.abs(lat) > 60 && Math.abs(lng) <= 60);

  if (couldBeSwapped) {
    const swappedLat = lng;
    const swappedLng = lat;
    lat = swappedLat;
    lng = swappedLng;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return { lat, lng };
};

const extractFromCoordinatePairString = (value) => {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const plainPairMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!plainPairMatch) return null;

  const first = asFiniteNumber(plainPairMatch[1]);
  const second = asFiniteNumber(plainPairMatch[2]);
  if (first === null || second === null) return null;

  if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return normalizeCoords({ lat: second, lng: first });
  }
  return normalizeCoords({ lat: first, lng: second });
};

const extractFromCoordinateArray = (value, isGeoJsonPoint = false) => {
  if (!Array.isArray(value) || value.length < 2) return null;

  const first = asFiniteNumber(value[0]);
  const second = asFiniteNumber(value[1]);
  if (first === null || second === null) return null;

  if (isGeoJsonPoint || Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return normalizeCoords({ lat: second, lng: first });
  }

  return normalizeCoords({ lat: first, lng: second });
};

const extractCoords = (record = {}) => {
  if (!record || typeof record !== 'object') return null;

  const nestedKeys = [
    'location',
    'coords',
    'customerCoords',
    'providerLocation',
    'ownerLocation',
    'geopoint',
    'geoPoint',
    'addressLocation',
    'selectedAddress',
    'profile',
    'provider_onboarding',
    'details',
    'customerLocation',
    'serviceLocation',
    'currentLocation',
    'position',
    'coordinates',
    'geo',
  ];

  const visited = new Set();
  const queue = [record];

  while (queue.length > 0) {
    const candidate = queue.shift();
    if (candidate == null) continue;

    if (typeof candidate === 'string') {
      const fromString = extractFromCoordinatePairString(candidate);
      if (fromString) return fromString;
      continue;
    }

    if (Array.isArray(candidate)) {
      const fromArray = extractFromCoordinateArray(candidate);
      if (fromArray) return fromArray;

      for (const item of candidate) {
        if (item && (typeof item === 'object' || typeof item === 'string')) {
          queue.push(item);
        }
      }
      continue;
    }

    if (typeof candidate !== 'object' || visited.has(candidate)) {
      continue;
    }
    visited.add(candidate);

    const direct = normalizeCoords(candidate);
    if (direct) return direct;

    if (Array.isArray(candidate.coordinates)) {
      const fromCoordinates = extractFromCoordinateArray(
        candidate.coordinates,
        String(candidate.type || '').toLowerCase() === 'point',
      );
      if (fromCoordinates) return fromCoordinates;
    }

    for (const key of nestedKeys) {
      if (candidate[key] != null) {
        queue.push(candidate[key]);
      }
    }
  }

  return null;
};

const formatDistanceLabel = (distance) => (
  Number.isFinite(distance) ? `${distance.toFixed(1)} km away` : 'distance unavailable'
);

const normalizeText = (value) => String(value || '').toLowerCase().trim();

const normalizeStatusValue = (value) => String(value || '').toLowerCase().trim();

const isTruthyStatusFlag = (value) => {
  if (value === true) return true;
  if (value === false || value == null) return false;

  const normalized = normalizeStatusValue(value);
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const getProviderLeadRestriction = (provider = {}) => {
  const profile = provider.profile || {};
  const accountStatus = normalizeStatusValue(provider.accountStatus || profile.accountStatus);
  const approvalStatus = normalizeStatusValue(provider.approvalStatus || profile.approvalStatus || 'approved');
  const passportStatus = normalizeStatusValue(provider.passportStatus || profile.passportStatus);
  const drivingLicenceStatus = normalizeStatusValue(
    provider.drivingLicenceStatus ||
    provider.drivingLicenseStatus ||
    profile.drivingLicenceStatus ||
    profile.drivingLicenseStatus,
  );
  const visaStatus = normalizeStatusValue(provider.visaStatus || profile.visaStatus);
  const disabledReason = String(provider.disabledReason || '').trim();

  const explicitlyDeactivated =
    provider.accountActive === false ||
    isTruthyStatusFlag(provider.inactive) ||
    accountStatus === 'deactivated';

  if (explicitlyDeactivated) {
    return { blocked: true, reason: 'deactivated' };
  }

  if (approvalStatus === 'pending') {
    return { blocked: true, reason: 'under-review' };
  }

  if (approvalStatus === 'rejected') {
    return { blocked: true, reason: 'rejected' };
  }

  if (passportStatus === 'blocked') {
    return { blocked: true, reason: 'passport-blocked' };
  }

  if (drivingLicenceStatus === 'blocked') {
    return { blocked: true, reason: 'driving-licence-blocked' };
  }

  if (visaStatus === 'blocked') {
    return { blocked: true, reason: 'visa-blocked' };
  }

  const anyDisableFlag =
    isTruthyStatusFlag(provider.providerAccountDisabled) ||
    isTruthyStatusFlag(provider.accountDisabled) ||
    isTruthyStatusFlag(provider.disabled);

  if (anyDisableFlag) {
    const accountLooksActive =
      provider.accountActive !== false &&
      !isTruthyStatusFlag(provider.inactive) &&
      (accountStatus === '' || accountStatus === 'active' || accountStatus === 'approved');
    const approvalLooksActive =
      approvalStatus === '' || approvalStatus === 'approved' || approvalStatus === 'active';

    if (accountLooksActive && approvalLooksActive && !disabledReason) {
      return { blocked: false, reason: null, staleProviderDisabledFlag: true };
    }

    return { blocked: true, reason: 'restricted' };
  }

  return { blocked: false, reason: null };
};

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const getProviderDisplayName = (provider = {}) => (
  provider.displayName ||
  provider.name ||
  provider.businessName ||
  provider.email ||
  'Unknown Provider'
);

const getProviderServiceDetails = (provider = {}) => {
  const combined = [
    ...normalizeList(provider.servicesOffered),
    ...normalizeList(provider.provider_onboarding?.servicesOffered),
    ...normalizeList(provider.profile?.servicesOffered),
    ...normalizeList(provider.serviceCategories),
    ...normalizeList(provider.serviceTypes),
    ...normalizeList(provider.skills),
    ...normalizeList(provider.categories),
  ];

  return Array.from(new Set(combined));
};

const getLeadServiceTerms = (lead = {}) => {
  const terms = [
    lead.serviceName,
    lead.subcategory,
    lead.leadSubcategory,
    lead.category,
    lead.leadCategory,
  ]
    .map((item) => String(item || '').trim())
    .filter(Boolean);

  return Array.from(new Set(terms));
};

const providerMatchesLeadService = (provider = {}, leadTerms = []) => {
  if (!leadTerms.length) return true;

  const providerServices = getProviderServiceDetails(provider).map(normalizeText);
  if (!providerServices.length) return false;

  const normalizedLeadTerms = leadTerms.map(normalizeText);
  return normalizedLeadTerms.some((term) => (
    providerServices.some((service) => service.includes(term) || term.includes(service))
  ));
};

const getLeadServiceName = (lead = {}) => (
  lead.leadSubcategory || lead.subcategory || lead.serviceName || 'Service'
);

const getLeadAreaText = (lead = {}) => {
  const directArea =
    lead.leadArea ||
    lead.area ||
    lead.locality ||
    lead.city ||
    lead.locationArea;

  if (directArea) return String(directArea).trim();

  const addressParts = String(lead.address || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (addressParts.length >= 3) return addressParts[addressParts.length - 2];
  if (addressParts.length === 2) return addressParts[1];
  if (addressParts.length === 1) return addressParts[0];
  return 'Area not provided';
};

/**
 * @param {object} [props.source] another country's data (see utils/indiaOperationsSource.js);
 *   without it the page reads the Australian database as before.
 */
export default function AdminAssignLeads({ source = null }) {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [providers, setProviders] = useState([]);
  const [assigning, setAssigning] = useState({});
  const [selectedMap, setSelectedMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [maxDistance, setMaxDistance] = useState(50);

  const getProvidersForLead = (lead) => {
    const leadTerms = getLeadServiceTerms(lead);
    const maxDistanceValue = Number(maxDistance);
    const leadCoords = extractCoords(lead);
    const hasLeadCoords = Number.isFinite(leadCoords?.lat) && Number.isFinite(leadCoords?.lng);

    return providers
      .map((provider) => {
        const leadRestriction = getProviderLeadRestriction(provider);
        const providerCoords = extractCoords(provider);
        const hasProviderCoords = Number.isFinite(providerCoords?.lat) && Number.isFinite(providerCoords?.lng);

        const distance = hasLeadCoords && hasProviderCoords
          ? calculateDistance(
            leadCoords.lat,
            leadCoords.lng,
            providerCoords.lat,
            providerCoords.lng,
          )
          : null;

        const serviceDetails = getProviderServiceDetails(provider);
        const serviceMatch = providerMatchesLeadService(provider, leadTerms);

        return {
          ...provider,
          leadRestriction,
          distance,
          serviceDetails,
          serviceMatch,
        };
      })
      .filter((provider) => !provider.leadRestriction?.blocked)
      .filter((provider) => {
        if (maxDistanceValue > 0 && Number.isFinite(provider.distance)) {
          return provider.distance <= maxDistanceValue;
        }
        return true;
      })
      .sort((providerA, providerB) => {
        const aHasDistance = Number.isFinite(providerA.distance);
        const bHasDistance = Number.isFinite(providerB.distance);

        if (aHasDistance && bHasDistance) {
          return providerA.distance - providerB.distance;
        }
        if (aHasDistance) return -1;
        if (bHasDistance) return 1;

        if (providerA.serviceMatch !== providerB.serviceMatch) {
          return providerA.serviceMatch ? -1 : 1;
        }

        return getProviderDisplayName(providerA)
          .toLowerCase()
          .localeCompare(getProviderDisplayName(providerB).toLowerCase());
      });
  };

  const getNearestProvider = (lead) => {
    const providersForLead = getProvidersForLead(lead);
    const matchingProviders = providersForLead.filter((provider) => provider.serviceMatch);
    return matchingProviders[0] || providersForLead[0] || null;
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      if (source) {
        const [leadList, providerList] = await Promise.all([source.loadLeads(), source.loadProviders()]);
        setLeads(leadList);
        setFilteredLeads(leadList);
        setProviders(providerList);
        return;
      }

      const leadsCol = collection(db, 'leads');
      const q = query(leadsCol, where('status', 'in', ['lead', 'open', 'offered']));
      const snap = await getDocs(q);
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });

      const customerIdsToLookup = Array.from(
        new Set(
          list
            .filter((lead) => !extractCoords(lead) && lead.customerId)
            .map((lead) => lead.customerId),
        ),
      );

      if (customerIdsToLookup.length > 0) {
        const customerCoordsPairs = await Promise.all(
          customerIdsToLookup.map(async (customerId) => {
            try {
              const customerSnap = await getDoc(doc(db, 'users', customerId));
              if (!customerSnap.exists()) return [customerId, null];
              const coords = extractCoords(customerSnap.data());
              return [customerId, coords];
            } catch (lookupError) {
              console.warn('Failed to read customer profile for coords:', customerId, lookupError);
              return [customerId, null];
            }
          }),
        );

        const customerCoordsMap = Object.fromEntries(
          customerCoordsPairs.filter(([, coords]) => Number.isFinite(coords?.lat) && Number.isFinite(coords?.lng)),
        );

        list = list.map((lead) => {
          if (extractCoords(lead)) return lead;
          const fallbackCoords = customerCoordsMap[lead.customerId];
          return fallbackCoords ? { ...lead, customerCoords: fallbackCoords } : lead;
        });
      }

      setLeads(list);
      setFilteredLeads(list);

      const usersCol = collection(db, 'users');
      const pQ = query(usersCol, where('roles.client', '==', true));
      const pSnap = await getDocs(pQ);
      let pList = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      pList = pList.filter((p) => p.approvalStatus === 'approved' || p.isApproved === true);

      pList = await Promise.all(
        pList.map(async (provider) => {
          if (extractCoords(provider)) return provider;

          try {
            const onboardingSnap = await getDoc(doc(db, 'users', provider.id, 'details', 'provider_onboarding'));
            if (!onboardingSnap.exists()) return provider;

            const onboardingData = onboardingSnap.data();
            const onboardingCoords = extractCoords(onboardingData);

            return {
              ...provider,
              provider_onboarding: onboardingData,
              ...(onboardingCoords ? { providerLocation: onboardingCoords } : {}),
            };
          } catch (lookupError) {
            console.warn('Failed to read provider onboarding for coords:', provider.id, lookupError);
            return provider;
          }
        }),
      );

      pList.sort((a, b) => {
        const nameA = (a.displayName || a.name || a.email || '').toLowerCase();
        const nameB = (b.displayName || b.name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setProviders(pList);
    } catch (err) {
      console.error('Error loading admin leads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  useEffect(() => {
    let filtered = leads;

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => String(lead.status || '') === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((lead) => (
        lead.customerName?.toLowerCase().includes(term) ||
        lead.customerPhone?.toLowerCase().includes(term) ||
        lead.serviceName?.toLowerCase().includes(term) ||
        lead.subcategory?.toLowerCase().includes(term) ||
        lead.leadSubcategory?.toLowerCase().includes(term) ||
        lead.id?.toLowerCase().includes(term)
      ));
    }

    setFilteredLeads(filtered);
  }, [searchTerm, statusFilter, leads]);

  const handleSelect = (leadId, providerId) => {
    setSelectedMap((prev) => ({ ...prev, [leadId]: providerId }));
  };

  const handleAssign = async (leadId, preferredProviderId = null) => {
    const lead = leads.find((item) => item.id === leadId);
    const nearestProvider = lead ? getNearestProvider(lead) : null;
    const providerId = preferredProviderId || selectedMap[leadId] || nearestProvider?.id;

    if (!providerId) {
      alert('No provider available for assignment within the current filters.');
      return;
    }

    const selectedProvider = providers.find((item) => item.id === providerId);
    const selectedProviderRestriction = selectedProvider ? getProviderLeadRestriction(selectedProvider) : null;
    if (selectedProviderRestriction?.blocked) {
      alert(`Selected provider is not eligible for leads (${selectedProviderRestriction.reason}). Please choose another provider.`);
      return;
    }

    setAssigning((prev) => ({ ...prev, [leadId]: true }));
    try {
      if (source) {
        await source.assignLead(leadId, providerId);
      } else {
        const resp = await fetch(`${CLOUD_FUNCTION_BASE}/assignLeadToProvider`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ leadId, providerId }),
        });

        const rawPayload = await resp.text();
        let json = {};
        try {
          json = rawPayload ? JSON.parse(rawPayload) : {};
        } catch (parseError) {
          json = { error: rawPayload || `Unexpected response (${resp.status})` };
        }

        if (!resp.ok || !json?.success) {
          const reasonText = [json?.error, json?.reason].filter(Boolean).join(' | ');
          throw new Error(reasonText || `Assign failed with status ${resp.status}`);
        }
      }

      setLeads((prev) => prev.filter((leadItem) => leadItem.id !== leadId));
      setSelectedMap((prev) => {
        const { [leadId]: _, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      console.error('Lead assign error', err);
      alert(`Assign error: ${err.message}`);
    } finally {
      setAssigning((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return 'N/A';
    return `${timestamp.toDate().toLocaleDateString()} ${timestamp.toDate().toLocaleTimeString()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'offered': return 'info';
      case 'accepted': return 'success';
      case 'lead': return 'warning';
      case 'open': return 'warning';
      default: return 'default';
    }
  };

  if (loading) return <Box p={3}><CircularProgress /></Box>;

  return (
    <Box p={1}>
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            placeholder="Search by customer, service, or lead ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="offered">Offered</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Max Distance</InputLabel>
            <Select
              value={maxDistance}
              label="Max Distance"
              onChange={(e) => setMaxDistance(Number(e.target.value))}
            >
              <MenuItem value={0}>All Providers</MenuItem>
              <MenuItem value={5}>Within 5 km</MenuItem>
              <MenuItem value={10}>Within 10 km</MenuItem>
              <MenuItem value={20}>Within 20 km</MenuItem>
              <MenuItem value={30}>Within 30 km</MenuItem>
              <MenuItem value={50}>Within 50 km</MenuItem>
              <MenuItem value={100}>Within 100 km</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredLeads.length} of {leads.length} leads
            {providers.length > 0 && ` • ${providers.length} approved providers available`}
          </Typography>
        </Box>
      </Card>

      {filteredLeads.length === 0 ? (
        <Alert severity="info">
          {leads.length === 0
            ? 'No leads available for assignment.'
            : 'No leads match your search criteria.'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredLeads.map((lead) => {
            const providersForLead = getProvidersForLead(lead);
            const matchingProviders = providersForLead.filter((provider) => provider.serviceMatch);
            const nearestProvider = matchingProviders[0] || providersForLead[0] || null;
            const selectedProvider = providersForLead.find((provider) => provider.id === selectedMap[lead.id]);

            return (
              <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={lead.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {getLeadServiceName(lead)}
                      </Typography>
                      <Chip
                        label={lead.status || 'lead'}
                        color={getStatusColor(lead.status)}
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {lead.customerName || 'Unknown Customer'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                        {lead.customerPhone || lead.customerEmail || 'No contact info'}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" noWrap>
                          {getLeadAreaText(lead)}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {lead.requestedDate || lead.selectedDate || formatDate(lead.createdAt)}
                          {lead.requestedTime || lead.selectedTime ? ` • ${lead.requestedTime || lead.selectedTime}` : ''}
                        </Typography>
                      </Box>

                      {(lead.providerPrice || lead.price) && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <MoneyIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>
                            {source
                              ? source.formatCurrency(lead.providerPrice || lead.price)
                              : `$${lead.providerPrice || lead.price}`}
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        Requested: {[lead.leadCategory || lead.category, lead.leadSubcategory || lead.subcategory].filter(Boolean).join(' / ') || 'General'}
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" gap={1} flexDirection="column">
                      {nearestProvider ? (
                        <Typography variant="caption" color="success.main">
                          Nearest available: {getProviderDisplayName(nearestProvider)}
                          {` (${formatDistanceLabel(nearestProvider.distance)})`}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="error.main">
                          No provider is available within the selected distance.
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {providersForLead.length} providers in range
                        {matchingProviders.length > 0 ? `, ${matchingProviders.length} service-matched` : ''}
                      </Typography>

                      <FormControl fullWidth size="small">
                        <InputLabel id={`select-${lead.id}`}>Assign Provider</InputLabel>
                        <Select
                          labelId={`select-${lead.id}`}
                          value={selectedMap[lead.id] || ''}
                          label="Assign Provider"
                          onChange={(e) => handleSelect(lead.id, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Select provider...</em>
                          </MenuItem>
                          {providersForLead.map((provider) => (
                            <MenuItem key={provider.id} value={provider.id}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {getProviderDisplayName(provider)}
                                  {` (${formatDistanceLabel(provider.distance)})`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color={provider.serviceMatch ? 'success.main' : 'text.secondary'}
                                  noWrap
                                >
                                  Services: {provider.serviceDetails.slice(0, 3).join(', ') || 'Not listed'}
                                  {!provider.serviceMatch ? ' | no exact service match' : ''}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {selectedProvider && (
                        <Typography variant="caption" color="text.secondary">
                          Selected provider services: {selectedProvider.serviceDetails.join(', ') || 'Not listed'}
                        </Typography>
                      )}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => handleAssign(lead.id, nearestProvider?.id || null)}
                          disabled={assigning[lead.id] || !nearestProvider}
                        >
                          {assigning[lead.id] ? 'Assigning...' : 'Assign Nearest'}
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleAssign(lead.id)}
                          disabled={assigning[lead.id] || (!selectedMap[lead.id] && !nearestProvider)}
                        >
                          {assigning[lead.id] ? 'Assigning...' : 'Assign Selected'}
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
